import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
// Submodule path: the backoffice barrel evaluates every backoffice screen, and this only needs the
// per-profile section list (which imports nothing but types).
import { backofficeGroupsForProfile } from '@/features/backoffice/sections';
import { THEME_LABELS, THEME_PREFERENCES, type ThemePreference } from '@/lib/theme';

import { navigationLayoutForProfile } from './navigation';
import styles from './CommandPalette.module.css';

interface Command {
  readonly id: string;
  readonly label: string;
  /** Where it lives, shown beside the label and searched along with it. */
  readonly group: string;
  readonly run: () => void;
}

const normalise = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/gu, '').toLowerCase();

function matches(command: Command, query: string): boolean {
  const haystack = normalise(`${command.label} ${command.group}`);
  return normalise(query)
    .split(/\s+/u)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The top bar's own setter, so the account menu and the palette never disagree on the theme. */
  readonly onTheme: (preference: ThemePreference) => void;
}

/**
 * ⌘K / Ctrl+K: type a few letters of any place the profile can reach — a top-bar destination, an
 * account page, any backoffice section — and Enter goes there. Built for the people who live in
 * the backoffice and know where they are going; it adds nothing a click could not already do,
 * and only lists what the profile's own navigation lists.
 *
 * Deliberately without animation: it is opened from the keyboard, many times a day, and anything
 * that delays the list between the shortcut and the next keystroke is in the way.
 */
export function CommandPalette({ open, onClose, onTheme }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => {
      void navigate(to, { viewTransition: true });
    };
    const layout = navigationLayoutForProfile(profile);
    const list: Command[] = [
      ...layout.primary.map((item) => ({
        id: item.to,
        label: item.label,
        group: 'Ir para',
        run: go(item.to),
      })),
      ...layout.menu.map((item) => ({
        id: item.to,
        label: item.label,
        group: 'Conta',
        run: go(item.to),
      })),
      ...backofficeGroupsForProfile(profile).flatMap((group) =>
        group.sections.map((section) => ({
          id: section.to,
          label: section.label,
          group: `Backoffice · ${group.label}`,
          run: go(section.to),
        })),
      ),
      ...THEME_PREFERENCES.map((option) => ({
        id: `tema-${option}`,
        label: `Tema: ${THEME_LABELS[option]}`,
        group: 'Aparência',
        run: () => {
          onTheme(option);
        },
      })),
    ];
    return list;
  }, [navigate, profile, onTheme]);

  const visible = useMemo(
    () => (query.trim() === '' ? commands : commands.filter((command) => matches(command, query))),
    [commands, query],
  );

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  if (!open) {
    return null;
  }

  const choose = (command: Command | undefined) => {
    if (command === undefined) {
      return;
    }
    onClose();
    command.run();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, visible.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(visible[active]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  const optionId = (index: number) => `${listId}-${String(index)}`;

  return createPortal(
    <div className={styles.overlay}>
      <button
        type="button"
        className={styles.backdrop}
        aria-label="Fechar a busca"
        tabIndex={-1}
        onClick={onClose}
      />
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Ir para">
        <input
          ref={inputRef}
          className={styles.input}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={visible.length > 0 ? optionId(active) : undefined}
          aria-autocomplete="list"
          placeholder="Ir para… (seção, página ou tema)"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={handleKeyDown}
        />
        {visible.length > 0 ? (
          <ul className={styles.list} id={listId} role="listbox" aria-label="Resultados">
            {visible.map((command, index) => (
              // Options are chosen through the input's keyboard handling (combobox pattern);
              // the click is the pointer path to the same choice.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={command.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === active}
                className={styles.option}
                onMouseMove={() => {
                  setActive(index);
                }}
                onClick={() => {
                  choose(command);
                }}
              >
                <span className={styles.label}>{command.label}</span>
                <span className={styles.group}>{command.group}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.empty}>Nada encontrado para “{query}”.</p>
        )}
        <p className={styles.keys}>
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> navegar
          </span>
          <span>
            <kbd>Enter</kbd> abrir
          </span>
          <span>
            <kbd>Esc</kbd> fechar
          </span>
        </p>
      </div>
    </div>,
    document.body,
  );
}
